import React from 'react';
import { 
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Chip, Box, 
  Grid, Divider, Link, List, ListItem, ListItemIcon, ListItemText, Paper
} from '@mui/material';
import { 
  Launch as LaunchIcon, 
  Language as WebsiteIcon, 
  Email as EmailIcon,
  Category as CategoryIcon,
  Tag as TagIcon,
  Games as PlatformIcon,
  Translate as LanguageIcon,
  Link as LinkIcon
} from '@mui/icons-material';
import parse from 'html-react-parser';

const GameDetailsDialog = ({ open, onClose, game }) => {
  if (!game) return null;

  const handleVisitSteamStore = () => {
    window.open(`https://store.steampowered.com/app/${game.appId}`, '_blank');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" component="div">
          {game.name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle1" gutterBottom>
              Confidence Rating: {Math.ceil(game.confidenceRating)}
            </Typography>
            <Typography variant="body2" paragraph>{game.shortDescription}</Typography>
            
            <Typography variant="subtitle2" gutterBottom>Developers:</Typography>
            <Typography variant="body2" paragraph>{game.developers.join(', ')}</Typography>
            
            <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Categories:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {game.categories.map((category) => (
                  <Chip key={category} label={category} size="small" icon={<CategoryIcon />} />
                ))}
              </Box>
            </Paper>
            
            <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Tags:</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {game.tags.map((tag) => (
                  <Chip key={tag} label={tag} size="small" icon={<TagIcon />} />
                ))}
              </Box>
            </Paper>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <List dense>
              <ListItem>
                <ListItemIcon><PlatformIcon /></ListItemIcon>
                <ListItemText 
                  primary="Platforms"
                  secondary={game.platforms.join(', ')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon><LanguageIcon /></ListItemIcon>
                <ListItemText 
                  primary="Supported Languages"
                  secondary={parse(game.supportedLanguages)}
                />
              </ListItem>
            </List>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="h6" gutterBottom>Contact Information</Typography>
            {game.website && (
              <Link href={game.website} target="_blank" rel="noopener noreferrer" display="block" sx={{ mb: 1 }}>
                <WebsiteIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
                Official Website
              </Link>
            )}
            {game.supportInfo?.email && (
              <Link href={`mailto:${game.supportInfo.email}`} display="block" sx={{ mb: 1 }}>
                <EmailIcon sx={{ mr: 1, verticalAlign: 'bottom' }} />
                {game.supportInfo.email}
              </Link>
            )}
            
            {game.links && Object.keys(game.links).length > 0 && (
              <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Links:</Typography>
                <Grid container spacing={1}>
                  {Object.entries(game.links).map(([platform, url]) => (
                    <Grid item xs={6} key={platform}>
                      <Link href={url} target="_blank" rel="noopener noreferrer" display="flex" alignItems="center">
                        <LinkIcon sx={{ mr: 1, fontSize: 'small' }} />
                        <Typography variant="body2">
                          {platform.charAt(0).toUpperCase() + platform.slice(1)}
                        </Typography>
                      </Link>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            )}
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleVisitSteamStore} color="primary" startIcon={<LaunchIcon />}>
          Visit Steam Store
        </Button>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default GameDetailsDialog;